/**
 * Gedeelde factuur-/bestelbon-renderer. ÉÉN implementatie voor zowel de klant-factuur (PDF-download
 * in de admin/account, geopend in de browser) als de bevestigingsmail (Cloud Functions). Zo zijn ze
 * gegarandeerd identiek — zelfde opbouw, zelfde bedragen, zelfde uitsplitsing per bewerking.
 *
 * Alle styling is INLINE (e-mailclients strippen <style>). De enige <style>-regels betreffen de
 * losse tekening-pagina's en @media print — die gelden alleen in de browser-PDF; e-mailclients
 * negeren ze én tonen geen tekeningen (Gmail/Outlook renderen sowieso geen inline-SVG).
 *
 * De prijsopbouw per glas-regel komt uit `OrderItem.breakdown` (bij het aanmaken opgeslagen). De
 * client kan voor oude orders een `breakdown` meegeven via `opts.breakdownFor`.
 */
import { COMPANY, isPlaceholder } from "./company";
import { stukLabel } from "./types/order";
import { KIYOH } from "./reviews-platform";
import { BTW_VERLEGD_LABEL, BTW_VERLEGD_TOELICHTING } from "./pricing/btw-verlegging";
import type { Order } from "./types/order";
import type { InvoiceLineBreakdown } from "./types/pricing";

const NAVY = "#013878";
/** Accentkleur voor het opmerkingenblok — dezelfde oranje als in de admin, zodat een opmerking
 *  in de mail en op het scherm hetzelfde signaal geeft. */
const ACCENT = "#e6561b";

const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] ?? c));

const eurFmt = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" });
const eur = (cents: number): string => eurFmt.format(cents / 100);

export interface InvoiceRenderOptions {
  /** Absolute logo-URL (browser: `${origin}/logo.png`; mail: publieke URL). Leeg = geen logo. */
  logoUrl?: string;
  /** Vooraf gebouwde tekening-pagina's (browser-PDF). Leeg in de mail. */
  drawPagesHtml?: string;
  /** CSS voor de tekening-pagina's (browser). Alleen relevant als drawPagesHtml gevuld is. */
  drawPagesCss?: string;
  /** Start de printdialoog zodra het logo geladen is (klant-download). Uit in de mail. */
  autoPrint?: boolean;
  /** Vertaalfunctie (client levert i18n; default = identiteit → Nederlands). */
  t?: (s: string) => string;
  /** Taalcode voor <html lang>. */
  locale?: string;
  /** Breakdown-resolver voor regels zonder opgeslagen `breakdown` (oude orders, client-catalogus). */
  breakdownFor?: (item: Order["items"][number]) => InvoiceLineBreakdown | null | undefined;
  /** Artikelnummer-resolver (client-catalogus) — optioneel. */
  articleNumberFor?: (item: Order["items"][number]) => string | undefined;
  /**
   * Eigen factuurnummer, voor een factuur die niet bij een webshop-bestelling hoort (`F-2026-001`).
   * Zonder deze optie is het nummer het ordernummer, zoals altijd.
   */
  invoiceNo?: string;
  /** Eigen factuurdatum (epoch ms); default is de besteldatum. */
  invoiceDate?: number;
  /**
   * Dit stuk is een **conceptfactuur**: nog geen nummer, dus nog geen boekstuk.
   *
   * Zonder deze vlag kreeg een concept de kop "Factuur", het woord CONCEPT op de plek van het
   * nummer én een overmaakverzoek met "o.v.v. factuurnummer CONCEPT" — een betaalinstructie met
   * een kenmerk dat niet bestaat. (Gemeld door Taco, 27-10-2026.) Een concept krijgt daarom een
   * eigen kop, geen nummerregel en geen betaalinstructie; de betaallink gaat apart mee.
   */
  concept?: boolean;
}

/** Bouwt de volledige factuur-HTML (identiek voor klant-download én bevestigingsmail). */
export function renderInvoiceHtml(order: Order, opts: InvoiceRenderOptions = {}): string {
  const t = opts.t ?? ((s: string) => s);
  /**
   * Factuur of bestelbon?
   *
   * Een factuurnummer wordt pas toegekend als er betaald is (`order.invoiceNumber`). Is dat er
   * niet, dan is dit document géén factuur maar een bestelbon: het toont het bestelnummer en
   * vraagt om betaling, en er staat nergens "factuur" of een factuurnummer op. Zo kan een
   * bestelling die nooit betaald wordt ook nooit als factuur in de administratie belanden.
   *
   * `opts.invoiceNo` wint: een losse factuur (montage, nalevering) levert zijn eigen nummer aan.
   */
  const isConcept = opts.concept === true;
  const isFactuur = !isConcept && (opts.invoiceNo != null || order.invoiceNo != null);
  const invoiceNo = opts.invoiceNo ?? stukLabel(order);
  const stuknaam = isConcept ? t("Conceptfactuur") : isFactuur ? t("Factuur") : t("Bestelbon");
  // Factuurdatum ALTIJD in Nederlandse tijd. Zonder `timeZone` volgt hij de klok van de machine
  // die de factuur maakt — en die factuur wordt óók serverzijdig gerenderd voor de
  // bevestigingsmail, waar Cloud Functions op UTC draaien. Een bestelling van 00:30 (NL) kreeg zo
  // de datum van de dag ervóór op de gemailde factuur. Rond een maand- of kwartaalgrens valt zo'n
  // factuur in de verkeerde btw-aangifte.
  const datumMs = opts.invoiceDate ?? order.createdAt;
  const dateStr = datumMs
    ? new Date(datumMs).toLocaleDateString(opts.locale === "en" ? "en-GB" : "nl-NL", {
        timeZone: "Europe/Amsterdam",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  // ---- Bedrijfsblok (placeholders weglaten) ----
  const coLine2 = [!isPlaceholder(COMPANY.kvk) ? `KvK ${esc(COMPANY.kvk)}` : "", !isPlaceholder(COMPANY.vat) ? `${t("Btw")} ${esc(COMPANY.vat)}` : ""].filter(Boolean).join(" · ");
  const coLine3 = [!isPlaceholder(COMPANY.email) ? esc(COMPANY.email) : "", !isPlaceholder(COMPANY.phone) ? esc(COMPANY.phone) : ""].filter(Boolean).join(" · ");
  const coAddr = [COMPANY.street, `${COMPANY.postalCode} ${COMPANY.city}`.trim(), COMPANY.country].filter((l) => !isPlaceholder(l));
  const companyBlock = [`<strong>${esc(COMPANY.legalName)}</strong>`, ...coAddr.map(esc), coLine2, coLine3].filter(Boolean).join("<br>");

  // ---- Klant (factuur aan) ----
  const c = order.customer;
  const a = c.address;
  const klant = [
    c.companyName ? esc(c.companyName) : "",
    esc(`${c.firstName} ${c.lastName}`.trim()),
    a ? esc(a.street) : "",
    a ? esc(`${a.postalCode} ${a.city}`.trim()) : "",
    a?.country === "BE" ? "België" : "",
    c.email ? esc(c.email) : "",
    c.kvk ? `KvK ${esc(c.kvk)}` : "",
    c.vatNumber ? `${t("Btw")} ${esc(c.vatNumber)}` : "",
  ].filter(Boolean).join("<br>");

  // ---- Regels ----
  const cellR = `padding:9px 6px;border-bottom:1px solid #e5e7eb;vertical-align:top;text-align:right;white-space:nowrap;`;
  const cellL = `padding:9px 6px;border-bottom:1px solid #e5e7eb;vertical-align:top;`;
  const subCell = `padding:2px 6px;color:#6b7280;font-size:12px;vertical-align:top;`;
  const subRow = (label: string, stuks: number, qty: number) =>
    `<tr><td style="${subCell}padding-left:22px;">${esc(label)}</td><td style="${subCell}text-align:right;"></td><td style="${subCell}text-align:right;white-space:nowrap;">${eur(stuks)}</td><td style="${subCell}text-align:right;white-space:nowrap;">${eur(stuks * qty)}</td></tr>`;

  const rows = order.items
    .map((it) => {
      const qty = it.quantity;
      const bd = it.breakdown ?? opts.breakdownFor?.(it) ?? undefined;
      if (bd) {
        const out = [
          `<tr><td style="${cellL}">${esc(it.productName)}${it.note ? `<div style="color:#9ca3af;font-size:11px;margin-top:2px;">${esc(it.note)}</div>` : ""}</td><td style="${cellR}">${qty}</td><td style="${cellR}">${eur(bd.glassNet)}</td><td style="${cellR}">${eur(bd.glassNet * qty)}</td></tr>`,
        ];
        if (bd.edgePrice > 0) out.push(subRow(bd.edgeLabel ? t(bd.edgeLabel) : t("Randafwerking"), bd.edgePrice, qty));
        for (const p of bd.processings) out.push(subRow(p.name, p.cost, qty));
        return out.join("");
      }
      // Het nummer dat bij de order is vastgelegd wint: dat is wat de klant destijds zag. De
      // callback is alleen terugval voor orders van vóór dat veld — een naslag in de catalogus
      // kan inmiddels een ander nummer opleveren (de doornummering schuift bij een sync).
      const artNr = it.articleNumber || opts.articleNumberFor?.(it);
      const extra = [artNr ? `${t("Artikelnr.")} ${esc(artNr)}` : "", it.note ? esc(it.note) : ""].filter(Boolean);
      return `<tr><td style="${cellL}">${esc(it.productName)}${extra.length ? `<div style="color:#9ca3af;font-size:11px;margin-top:2px;">${extra.join(" · ")}</div>` : ""}</td><td style="${cellR}">${qty}</td><td style="${cellR}">${eur(it.unitPrice)}</td><td style="${cellR}">${eur(it.linePrice)}</td></tr>`;
    })
    .join("");

  // ---- Totalen ----
  const tot = order.totals;
  // Btw verlegd (intracommunautaire levering): dan hoort er geen btw-bedrag op de factuur maar de
  // vermelding "Btw verlegd", mét beide btw-nummers en de wettelijke verwijzing. Zonder die
  // vermelding is de factuur formeel niet in orde, ook al klopt het bedrag.
  const verlegd = order.btw?.verlegd === true;
  const totRow = (label: string, value: string, style = "") => `<tr><td style="padding:4px 6px;${style}">${label}</td><td style="padding:4px 6px;text-align:right;white-space:nowrap;${style}">${value}</td></tr>`;
  const totals = `<table role="presentation" width="280" cellpadding="0" cellspacing="0" style="margin:16px 0 0 auto;font-size:13px;">
    ${totRow(t("Subtotaal (excl. btw)"), eur(tot.subtotal))}
    ${tot.discount != null ? totRow(`${t("Korting")}${order.discount?.kind === "percent" ? ` (${order.discount.value}%)` : ""}`, "− " + eur(tot.discount), "color:#15803d;") : ""}
    ${totRow(t("Verzendkosten"), eur(tot.shipping))}
    ${verlegd ? totRow(t(BTW_VERLEGD_LABEL), "€ 0,00") : totRow(t("Btw 21%"), eur(tot.vat))}
    <tr><td colspan="2" style="border-top:2px solid ${NAVY};font-size:0;line-height:0;">&nbsp;</td></tr>
    <tr><td style="padding:8px 6px;font-weight:700;color:${NAVY};font-size:15px;">${verlegd ? t("Totaal") : t("Totaal (incl. btw)")}</td><td style="padding:8px 6px;text-align:right;white-space:nowrap;font-weight:700;color:${NAVY};font-size:15px;">${eur(tot.total)}</td></tr>
  </table>
  ${verlegd
    ? `<p style="margin:10px 0 0 auto;max-width:420px;text-align:right;font-size:11px;line-height:1.5;color:#475569;">
        ${esc(t(BTW_VERLEGD_TOELICHTING))}<br>
        ${esc(t("Btw-nummer afnemer"))}: <strong>${esc(order.btw?.vatNumber ?? order.customer.vatNumber ?? "")}</strong> ·
        ${esc(t("Btw-nummer leverancier"))}: <strong>${esc(COMPANY.vat)}</strong>
      </p>`
    : ""}`;

  // ---- Betaalblok ----
  // Een factuur hoort te zeggen of er nog iets openstaat. Is de bestelling betaald (alles behalve
  // "wacht op betaling" en een offerte), dan is dit een betaalbewijs en vragen we niet alsnog om
  // een overboeking. Staat 'ie wél open én kennen we een IBAN, dan het overmaakverzoek.
  const openstaand = order.status === "awaiting_payment" || order.status === "offerte";
  // De order wordt pas aangemaakt nadat Mollie "betaald" meldt, dus de orderdatum is de betaaldatum.
  const betaaldOp = dateStr;
  const payBlock = !openstaand
    ? `<div style="margin-top:36px;padding:14px 16px;background:#f0fdf4;border:1px solid #bbf7d0;color:#166534;line-height:1.6;font-size:12px;">
        <strong>${t("Voldaan")}${betaaldOp ? ` — ${betaaldOp}` : ""}.</strong> ${t("Dit bedrag is online betaald; deze factuur geldt als betaalbewijs.")}
      </div>`
    : // Een bestelbon heeft geen nummer om bij een overboeking te vermelden en wordt via de
      // betaallink afgerekend. Een overmaakverzoek met IBAN hoort hier dus niet; dat geldt alleen
      // voor een losse factuur (montage), die wél een nummer heeft. (Taco, 27-10-2026.)
      isConcept
      ? `<div style="margin-top:36px;padding:14px 16px;background:#f3f6fb;color:#374151;line-height:1.6;font-size:12px;">
        ${t("Dit is een conceptfactuur en nog geen boekstuk. Na betaling ontvangt u de definitieve factuur met factuurnummer.")}
      </div>`
      : !isFactuur
      ? `<div style="margin-top:36px;padding:14px 16px;background:#f3f6fb;color:#374151;line-height:1.6;font-size:12px;">
        ${t("Dit is nog geen factuur. Zodra uw bestelling betaald is, ontvangt u de factuur met factuurnummer.")}
      </div>`
      : !isPlaceholder(COMPANY.iban)
      ? `<div style="margin-top:36px;padding:14px 16px;background:#f3f6fb;color:#374151;line-height:1.6;font-size:12px;">
        ${t("Gelieve het totaalbedrag over te maken op")} <strong>${esc(COMPANY.iban)}</strong> ${t("t.n.v.")} ${esc(COMPANY.legalName)}, ${t("o.v.v. factuurnummer")} ${invoiceNo}.
      </div>`
      : "";

  const logo = opts.logoUrl
    ? `<img src="${esc(opts.logoUrl)}" alt="Glastotaal" style="height:48px;width:auto;display:block;"${opts.autoPrint ? ` onload="window.print()" onerror="this.style.display='none';window.print()"` : ""}>`
    : `<span style="font-size:22px;font-weight:700;color:${NAVY};">Glastotaal</span>`;

  const head = `<style>${opts.drawPagesCss ?? ""}@media print{body{padding:0!important;}}</style>`;

  /**
   * Opmerking van de klant, terug in de bevestiging.
   *
   * Twee redenen. De klant ziet zwart-op-wit dat we het hebben ontvangen — anders blijft het
   * gissen of die opmerking ergens is aangekomen. En dezelfde HTML gaat als melding naar ons
   * team, dus staat de opmerking meteen in de mail waarmee we de bestelling oppakken, in plaats
   * van alleen in de admin.
   */
  const opmerkingBlok = order.customerNote
    ? `<div style="margin-top:18px;border-left:4px solid ${ACCENT};background:#fff7f2;padding:10px 12px;">
         <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:#9a3412;">${t("Uw opmerking bij deze bestelling")}</div>
         <div style="margin-top:4px;color:#1f2937;font-size:13px;white-space:pre-wrap;">${esc(order.customerNote)}</div>
       </div>`
    : "";

  return `<!doctype html><html lang="${esc(opts.locale ?? "nl")}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${stuknaam}${isFactuur ? ` ${invoiceNo}` : ""}</title>${head}</head>
<body style="font-family:Arial,Helvetica,sans-serif;color:#1f2937;margin:0;padding:40px;font-size:13px;background:#fff;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>
    <td style="vertical-align:top;">${logo}</td>
    <td style="vertical-align:top;text-align:right;color:#4b5563;line-height:1.5;font-size:12px;">${companyBlock}</td>
  </tr></table>

  <h1 style="font-size:20px;margin:32px 0 0;color:${NAVY};">${stuknaam}</h1>
  <div style="margin-top:4px;color:#6b7280;">${isFactuur
    ? `${t("Factuurnummer")} ${invoiceNo}${dateStr ? ` · ${t("Factuurdatum")} ${esc(dateStr)}` : ""}`
    : dateStr ? `${isConcept ? t("Datum") : t("Besteldatum")} ${esc(dateStr)}` : ""}</div>

  <div style="margin-top:24px;">
    <div style="font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#9ca3af;margin:0 0 6px;">${isFactuur ? t("Factuur aan") : t("Bestelling voor")}</div>
    <div style="line-height:1.5;">${klant || "—"}</div>
  </div>

  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin-top:28px;border-collapse:collapse;">
    <tr>
      <th style="text-align:left;border-bottom:2px solid ${NAVY};padding:8px 6px;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#6b7280;">${t("Omschrijving")}</th>
      <th style="text-align:right;border-bottom:2px solid ${NAVY};padding:8px 6px;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#6b7280;">${t("Aantal")}</th>
      <th style="text-align:right;border-bottom:2px solid ${NAVY};padding:8px 6px;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#6b7280;">${t("Stuksprijs")}</th>
      <th style="text-align:right;border-bottom:2px solid ${NAVY};padding:8px 6px;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:#6b7280;">${t("Totaal")}</th>
    </tr>
    ${rows}
  </table>

  ${totals}
  ${opmerkingBlok}
  ${payBlock}

  <div style="margin-top:28px;color:#9ca3af;font-size:11px;text-align:center;">${esc(COMPANY.name)} · ${t("Bedankt voor uw bestelling.")}</div>
  <div style="margin-top:4px;color:#9ca3af;font-size:11px;text-align:center;">${t("Tevreden over uw bestelling? Laat het ons weten op")} <a href="${esc(KIYOH.writeReviewUrl)}" style="color:#6b7280;">Kiyoh</a>.</div>
  ${opts.drawPagesHtml ?? ""}
</body></html>`;
}
